import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { Icon } from '@iconify/react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';

import { RouterLink } from 'src/routes/components';
import { useResponsive } from 'src/hooks/use-responsive';

import { E_TOOLS_NAV } from './config';

export default function EToolsNav({ openNav, onCloseNav }) {
  const { t } = useTranslation();
  const { lang = 'uz' } = useParams();
  const location = useLocation();
  const lgUp = useResponsive('up', 'lg');
  const [cookies] = useCookies(['user']);
  const isToolManager = cookies?.user?.tool_manager;
  const role = cookies?.user?.role;

  const items = useMemo(
    () => [
      {
        key: 'dashboard',
        title: t('eTools.nav.dashboard'),
        path: `/${lang}/e-tools/dashboard`,
        icon: 'solar:widget-5-bold-duotone',
      },
      {
        key: 'instruments',
        title: t('eTools.nav.instruments'),
        path: `/${lang}/e-tools/instruments`,
        icon: 'solar:settings-bold-duotone',
      },
      {
        key: 'borrow-records',
        title: t('eTools.nav.borrowRecords'),
        path: `/${lang}/e-tools/borrow-records`,
        icon: 'solar:clipboard-list-bold-duotone',
      },
      {
        key: 'employees',
        title: t('eTools.nav.employees'),
        path: `/${lang}/e-tools/employees`,
        icon: 'solar:users-group-two-rounded-bold-duotone',
      },
    ],
    [t, lang]
  );

  const content = (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}
    >
      {/* Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.75,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.25,
            background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon icon="solar:settings-bold" width={16} color="#fff" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: 'primary.dark', lineHeight: 1.2, fontSize: '0.8125rem' }}
          >
            E-Tools
          </Typography>
          <Typography
            sx={{ color: 'text.disabled', display: 'block', fontSize: '0.625rem', lineHeight: 1.4 }}
          >
            {t('eTools.nav.subtitle')}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={isToolManager ? t('eTools.nav.manager') : t('eTools.nav.viewer')}
          color={isToolManager ? 'success' : 'default'}
          variant="outlined"
          sx={{
            ml: 'auto',
            fontSize: 9,
            height: 18,
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Box>

      {/* Scrollable content area */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Section label */}
        <Typography
          variant="overline"
          sx={{
            px: 2,
            pt: 1.5,
            pb: 0.5,
            fontSize: '0.6rem',
            fontWeight: 700,
            color: 'text.disabled',
            letterSpacing: 1.2,
            display: 'block',
          }}
        >
          {t('eTools.nav.menu', 'MENU')}
        </Typography>

        {/* Nav items */}
        <Stack component="nav" spacing={0.5} sx={{ px: 1, py: 0.5 }}>
          {items.map((item) => {
            const active = location.pathname === item.path;
            return (
              <ListItemButton
                key={item.key}
                component={RouterLink}
                href={item.path}
                sx={{
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.75,
                  minHeight: 40,
                  gap: 1.25,
                  transition: 'all 0.15s ease-in-out',
                  color: active ? 'primary.dark' : 'text.secondary',
                  bgcolor: active
                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                  ...(active && {
                    borderLeft: '2px solid',
                    borderColor: 'primary.main',
                  }),
                  '&:hover': {
                    bgcolor: active
                      ? (theme) => alpha(theme.palette.primary.main, 0.12)
                      : (theme) => alpha(theme.palette.grey[500], 0.06),
                    color: 'text.primary',
                  },
                }}
              >
                <Icon icon={item.icon} width={18} />
                <Typography variant="body2" sx={{ fontWeight: active ? 700 : 500 }} noWrap>
                  {item.title}
                </Typography>
              </ListItemButton>
            );
          })}
        </Stack>
      </Box>

      {/* Switch project */}
      {role === 'admin' && (
        <Box sx={{ px: 1, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <ListItemButton
            component={RouterLink}
            href="/select-project"
            sx={{
              borderRadius: 1.5,
              px: 1.5,
              py: 0.75,
              minHeight: 40,
              gap: 1.25,
              transition: 'all 0.15s ease-in-out',
              color: 'text.secondary',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.06),
                color: 'text.primary',
              },
            }}
          >
            <Icon icon="solar:transfer-horizontal-bold-duotone" width={18} />
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {t('eTools.nav.switchProject')}
            </Typography>
          </ListItemButton>
        </Box>
      )}
    </Box>
  );

  if (lgUp) {
    return (
      <Box
        sx={{
          width: E_TOOLS_NAV.WIDTH,
          flexShrink: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Drawer
      open={openNav}
      onClose={onCloseNav}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { width: E_TOOLS_NAV.WIDTH } }}
    >
      {content}
    </Drawer>
  );
}

EToolsNav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};
